import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Stack, TextField } from "@mui/material";
import axios from "axios";
import { useContext, useEffect, useState } from "react";
import { CloseIcon } from "../../../minimals/theme/overrides/CustomIcons";
import { UserContext } from "../../contexts/UserContext";
import { getLedgerInfo } from "../../reports/dashboard/utils/nonZeroLedgerBalancesUtils";
import InternalUserAutocomplete from "../../shared/InternalUserAutocomplete";
import { InternalUser } from "../../types/app";
import { fCurrency } from "../../utils/formatNumber";
import { PriorityChip } from "../PriorityStatus";
import { saveOutstandingCheckLedgerNote } from "../saveNote";
import { Check, DashboardNote, IAssignable } from "../types";
import NotesTimeline from "./NotesTimeline";
import MarkDeadModal from "./MarkDeadModal";
import { canManageDeadChecks } from "../canManageDeadChecks";
import { getNotes, saveAssignToInternalUser, saveAssignToInternalUserForNonZeroLedgers, saveNote } from "./NotesUtils";
import { DashboardReportType } from "../../types/reports";

interface IProps {
  entity: IAssignable;
  setEntity: Function;
  setEntityTable?: Function;
  isOpen: boolean;
  orderNumberDisplay?: string;
  amount?: string;
  priority?: string;
  nameBeforeUpdate: string;
  emailBeforeUpdate: string;
  refetchData?: Function;
  reportType: DashboardReportType;
  saveLedgerAssignTo?: (assignedToUser: InternalUser) => Promise<void>;
  saveLedgerNote?: (note: string) => Promise<void>;
}

export default function NotesModal({
  entity,
  setEntity,
  setEntityTable,
  orderNumberDisplay,
  amount,
  priority,
  isOpen,
  nameBeforeUpdate = "",
  emailBeforeUpdate = "",
  refetchData,
  reportType,
  saveLedgerAssignTo,
  saveLedgerNote
}: IProps) {
  const isEscrowOverdue = reportType === DashboardReportType.EscrowOverdue;
  const [submitting, setSubmitting] = useState(false);
  const { user } = useContext(UserContext);
  const [markDeadOpen, setMarkDeadOpen] = useState(false);
  const canMarkDead = reportType === DashboardReportType.OutstandingChecks && entity.priority !== "Dead" &&
    canManageDeadChecks(user);
  const [text, setText] = useState<string>("");
  const [selectedAssignedTo, setSelectedAssignedTo] = useState<InternalUser | null>({
    fullName: nameBeforeUpdate || "",
    email: emailBeforeUpdate || ""
  } as InternalUser);

  const fetchAndSetAssignedToUser = async (selectedAssignedToEmail: string) => {
    if (!selectedAssignedToEmail) return;
    const { data } = await axios.get(
      `/api/clientphonebook/internalusers/getInternalUserByEmail?email=${selectedAssignedToEmail}`
    );
    if (data) {
      setSelectedAssignedTo(data);
    }
  };

  const saveAssignTo = async (assignToUser: InternalUser | null) => {
    if (!assignToUser || assignToUser.email?.toLowerCase() == emailBeforeUpdate?.toLowerCase()) return;

    if (isEscrowOverdue && saveLedgerAssignTo) {
      await saveLedgerAssignTo(assignToUser);
    } else {
      await saveAssignToInternalUser({
        uniqueIdentifier: entity.uniqueIdentifier,
        orderNumber: entity.orderNumber,
        user: assignToUser,
        reportType
      });
    }

    await updateSelectedEntity("assignedTo");
  };

  const handleSaveNote = async () => {
    if (!text) return;
    if (isEscrowOverdue && saveLedgerNote) {
      await saveLedgerNote(text);
    } else {
      await saveNote({
        uniqueIdentifier: entity.uniqueIdentifier,
        orderNumber: entity.orderNumber,
        text,
        reportType
      });
      if (reportType === DashboardReportType.OutstandingChecks) {
        await saveOutstandingCheckLedgerNote(user?.name || "", text, entity as unknown as Check);
      }
    }
    await updateSelectedEntity("note");
  };

  const updateEntityForNonZeroLedgerBalance = async () => {
    const ledgerIdentifier = entity?.name || entity?.orderNumber;
    if (!ledgerIdentifier) {
      alert("Order Number Not Found");
      return;
    }
    const updatedLedger = await getLedgerInfo(ledgerIdentifier);
    const updatedRow = { ...entity, ...updatedLedger };
    setEntity(updatedRow);

    setSelectedAssignedTo({
      fullName: updatedLedger.assignedTo || "",
      email: updatedLedger.assignedToEmail || ""
    } as InternalUser);

    await fetchAndSetAssignedToUser(updatedLedger.assignedToEmail);

    if (setEntityTable) {
      setEntityTable((tableData: IAssignable[]) => {
        return tableData.map((row) => {
          const rowIdentifier = row.name || row.orderNumber;
          return rowIdentifier == ledgerIdentifier ? updatedRow : row;
        });
      });
    }
    setText("");
  };

  const updateSelectedEntity = async (type: "note" | "assignedTo") => {
    if (!entity) return;
    let notes = entity.notes;
    if (type === "note" && isEscrowOverdue) {
      notes =
        type === "note"
          ? [
              ...entity.notes,
              {
                id: 0,
                // selectId: entity.selectId,
                uniqueIdentifier: entity.uniqueIdentifier,
                orderNumber: entity.orderNumber,
                createdByUser: user?.name,
                text,
                dateCreated: new Date(),
                createdBy: user?.name
              } as DashboardNote
            ]
          : entity.notes;
    } else if (reportType !== DashboardReportType.EscrowOverdue) {
      notes = await getNotes({
        orderNumber: entity.orderNumber,
        uniqueIdentifier: entity.uniqueIdentifier,
        reportType
      });
    }
    const updatedEntity = {
      ...entity,
      notes,
      assignedToUser: selectedAssignedTo,
      assignedToEmail: selectedAssignedTo?.email,
      assignedToName: selectedAssignedTo?.fullName,
      assignedTo: selectedAssignedTo?.fullName
    };
    setEntity(updatedEntity);
    if (setEntityTable) {
      setEntityTable((tableData: IAssignable[]) => {
        return isEscrowOverdue
          ? tableData.map((row) => (row.name == entity.name ? updatedEntity : row))
          : tableData.map((row) => (row.uniqueIdentifier == entity.uniqueIdentifier ? updatedEntity : row));
      });
    }
    setText("");
  };

  const handleNonZeroLedgerBalanceSubmit = async (assignToUser: InternalUser | null) => {
    let isChangesSubmited = false;
    if (assignToUser && assignToUser.email?.toLowerCase() != emailBeforeUpdate?.toLowerCase()) {
      await saveAssignToInternalUserForNonZeroLedgers({
        assignToUser,
        uniqueIdentifier: entity.uniqueIdentifier,
        orderNumber: entity.orderNumber,
        appUser: user
      });
      isChangesSubmited = true;
    }

    if (saveLedgerNote && text) {
      await saveLedgerNote(text);
      isChangesSubmited = true;
    }

    if (isChangesSubmited) {
      await updateEntityForNonZeroLedgerBalance();
    }
    return;
  };

  const save = async () => {
    if (!(selectedAssignedTo?.email || text) || !entity) return;
    setSubmitting(true);
    if (reportType == DashboardReportType.NonZeroLedgerBalances) {
      ////I'm done trying to twist this code to be reusable and work with every use case. I am making a specific save function for non zero ledger balances
      await handleNonZeroLedgerBalanceSubmit(selectedAssignedTo);
      setSubmitting(false);
      return;
    }
    await saveAssignTo(selectedAssignedTo);
    await handleSaveNote();
    setSubmitting(false);
    if (refetchData) {
      refetchData();
    }
  };

  useEffect(() => {
    fetchAndSetAssignedToUser(selectedAssignedTo?.email || "");
  }, []);

  const onClose = () => setEntity(null);

  const onMarkedDead = () => {
    setMarkDeadOpen(false);
    const updatedEntity = { ...entity, priority: "Dead" };
    setEntity(updatedEntity);
    if (setEntityTable) {
      setEntityTable((rows: IAssignable[]) => rows.map((row) =>
        row.uniqueIdentifier === entity.uniqueIdentifier ? { ...row, priority: "Dead" } : row));
    }
    refetchData?.();
  };

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Stack direction="row" spacing={2} alignItems="center" marginBottom={3}>
          <Box>
            Order Number {orderNumberDisplay || entity.orderNumber}
            {amount ? ` - ${fCurrency(amount)}` : ""}
          </Box>
          {priority && (
            <Box>
              <PriorityChip priority={priority} />
            </Box>
          )}
        </Stack>
        <InternalUserAutocomplete
          sx={{ width: 220 }}
          value={selectedAssignedTo}
          onChange={(val) => {
            setSelectedAssignedTo(val as InternalUser | null);
          }}
          label="Assigned to"
          limitToAccountReps
        />
      </DialogTitle>
      <IconButton
        onClick={onClose}
        sx={{ position: "absolute", right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent sx={{ paddingBottom: 3 }}>
        <Box paddingTop={1}>
          <TextField
            value={text}
            onChange={(e) => setText(e.target.value)}
            fullWidth
            multiline
            rows={3}
            label="Note"
            sx={{ marginBottom: 2 }}
          />
          <Stack direction="row" justifyContent="space-between">
            <Box>
              {canMarkDead && (
                <Button size="small" onClick={() => setMarkDeadOpen(true)} disabled={submitting}>Mark dead</Button>
              )}
            </Box>
            <Button variant="contained" size="small" onClick={save} disabled={submitting}>
              {submitting ? "Saving..." : "Save"}
            </Button>
          </Stack>
        </Box>
        <NotesTimeline notes={entity?.notes || []} />
      </DialogContent>
      {markDeadOpen && <MarkDeadModal entity={entity} onClose={() => setMarkDeadOpen(false)} onSaved={onMarkedDead} />}
    </Dialog>
  );
}
